/**
 * Maskering av känsliga URL-parametrar innan något loggas.
 *
 * Bakgrund: flera flöden bär hemligheter i query-strängen.
 * - Inbjudningslänken är `/inbjudan?token=<engångstoken>`.
 * - Supabase e-postbekräftelse och lösenordsåterställning använder
 *   `?token_hash=…&type=recovery`. Läcker den till en logg kan någon med
 *   loggåtkomst ta över kontot.
 * - OAuth-callbacks bär `code`, `access_token`, `refresh_token`,
 *   `provider_token` och `provider_refresh_token`.
 *
 * Regeln här är enkel: parametrar i SENSITIVE_PARAMS byts alltid mot
 * "[redacted]" — vi behåller att parametern fanns (bra för felsökning) men
 * aldrig dess värde. Matchningen är skiftlägesokänslig, och sker även när
 * parametern ligger nästlad i ett `next`- eller `redirect_to`-värde.
 */

export const SENSITIVE_PARAMS = new Set([
  "token",
  "token_hash",
  "access_token",
  "refresh_token",
  "provider_token",
  "provider_refresh_token",
  "code",
  "secret",
  "password",
  "api_key",
  "apikey",
  "key",
  "signature",
  "sig",
  "invite",
  "invitation",
  "otp",
  "ticket",
  "confirmation_token",
  "recovery_token",
  "id_token",
]);

/** Parametrar vars värde självt är en URL och därför maskeras rekursivt. */
export const NESTED_URL_PARAMS = new Set(["next", "redirect_to", "redirect", "redirect_uri", "return_to", "returnto"]);

const REDACTED = "[redacted]";

function redactSearchParams(params: URLSearchParams): boolean {
  let changed = false;
  for (const key of Array.from(new Set(params.keys()))) {
    const lower = key.toLowerCase();
    if (SENSITIVE_PARAMS.has(lower)) {
      params.set(key, REDACTED);
      changed = true;
    }
    // Nästlade värden (t.ex. next=/inbjudan?token=… eller
    // redirect_to=https://…/auth?token_hash=…) maskeras rekursivt.
    else if (NESTED_URL_PARAMS.has(lower)) {
      const value = params.get(key);
      if (value) {
        const masked = redactUrl(value);
        if (masked !== value) {
          params.set(key, masked);
          changed = true;
        }
      }
    }
  }
  return changed;
}

/**
 * Maskerar känsliga parametrar i en absolut URL eller en relativ path.
 * Hanterar även fragment-baserade tokens (`#access_token=…`), som Supabase
 * implicit flow använder.
 * Returnerar värdet oförändrat om det inte går att tolka som URL.
 */
export function redactUrl(input: string | null | undefined): string {
  if (!input) return input ?? "";
  try {
    const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(input);
    const url = new URL(input, "https://redact.local");
    redactSearchParams(url.searchParams);
    // URLSearchParams kodar om "[" och "]"; gör loggen läsbar igen.
    const search = url.search.replace(/%5B/gi, "[").replace(/%5D/gi, "]");

    // Hash kan bära `#access_token=…&refresh_token=…`.
    let hash = url.hash;
    if (hash.includes("=")) {
      const hashParams = new URLSearchParams(hash.slice(1));
      if (redactSearchParams(hashParams)) {
        hash = `#${hashParams.toString().replace(/%5B/gi, "[").replace(/%5D/gi, "]")}`;
      }
    }

    return isAbsolute
      ? `${url.origin}${url.pathname}${search}${hash}`
      : `${url.pathname}${search}${hash}`;
  } catch {
    return input;
  }
}

/** Maskerar ett enskilt parametervärde när parameternamnet är känt. */
export function redactParamValue(param: string, value: unknown): unknown {
  const lower = param.toLowerCase();
  if (SENSITIVE_PARAMS.has(lower)) return REDACTED;
  if (typeof value === "string" && NESTED_URL_PARAMS.has(lower)) return redactUrl(value);
  return value;
}

/** True om strängen ser ut som en URL eller path som är värd att maskera. */
function looksLikeUrl(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || value.startsWith("/") || value.includes("?") || value.includes("#");
}

/**
 * Djupmaskering av godtycklig struktur (Sentry-event, breadcrumb-data,
 * GA4-parametrar, felkontext). Nycklar som matchar SENSITIVE_PARAMS maskeras
 * helt; strängvärden som ser ut som URL:er körs genom redactUrl().
 */
export function redactDeep<T>(input: T, depth = 0): T {
  if (depth > 6) return input;
  if (typeof input === "string") {
    return (looksLikeUrl(input) ? redactUrl(input) : input) as unknown as T;
  }
  if (Array.isArray(input)) {
    return input.map((v) => redactDeep(v, depth + 1)) as unknown as T;
  }
  if (input && typeof input === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      if (SENSITIVE_PARAMS.has(k.toLowerCase())) out[k] = REDACTED;
      else if (typeof v === "string" && NESTED_URL_PARAMS.has(k.toLowerCase())) out[k] = redactUrl(v);
      else out[k] = redactDeep(v, depth + 1);
    }
    return out as unknown as T;
  }
  return input;
}

/**
 * Personuppgiftsnycklar. Sentrys standarduppsättning skickar användar-id,
 * e-post, IP och HTTP-kroppar; vi stänger av det i konfigurationen OCH
 * maskerar här som andra spärr. Allt som ser ut som ett formulärfält
 * (namn, e-post, telefon, meddelande, fritext) ersätts med [redacted] innan
 * något event lämnar oss.
 */
export const PII_KEYS = new Set([
  "email",
  "e-post",
  "epost",
  "mail",
  "user",
  "user_id",
  "userid",
  "username",
  "name",
  "namn",
  "firstname",
  "lastname",
  "fornamn",
  "efternamn",
  "fullname",
  "phone",
  "telefon",
  "tel",
  "mobil",
  "message",
  "meddelande",
  "body",
  "content",
  "text",
  "comment",
  "kommentar",
  "motivering",
  "fritext",
  "address",
  "adress",
  "personnummer",
  "ssn",
  "ip",
  "ip_address",
  "cookies",
  "cookie",
  "authorization",
  "data",
  "formdata",
  "form_data",
  "payload",
]);

/**
 * Djupmaskering för telemetri: URL-parametrar OCH personuppgifter.
 * Används på alla vägar ut mot Sentry och felinsamlaren.
 */
export function redactPii<T>(input: T, depth = 0): T {
  if (depth > 6) return input;
  if (Array.isArray(input)) return input.map((v) => redactPii(v, depth + 1)) as unknown as T;
  if (input && typeof input === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      const lower = k.toLowerCase();
      if (PII_KEYS.has(lower) || SENSITIVE_PARAMS.has(lower)) out[k] = REDACTED;
      else out[k] = redactPii(redactDeep(v, depth + 1), depth + 1);
    }
    return out as unknown as T;
  }
  return redactDeep(input, depth);
}
